import type {
  MetroStation,
  Place,
  PlaceFilters,
  StationCatalog,
} from './types';

export function normalizeSearchText(value: string): string {
  return value
    .normalize('NFKC')
    .toLocaleLowerCase('ru-RU')
    .replaceAll('ё', 'е')
    .trim()
    .replace(/\s+/gu, ' ');
}

export function getStationFromCatalog(
  catalog: StationCatalog,
  stationId: string,
): MetroStation | undefined {
  if (Array.isArray(catalog)) {
    return catalog.find((station) => station.id === stationId);
  }

  return (catalog as ReadonlyMap<string, MetroStation>).get(stationId);
}

export function placeMatchesSearch(
  place: Place,
  query: string,
  catalog: StationCatalog,
): boolean {
  const normalisedQuery = normalizeSearchText(query);
  if (normalisedQuery.length === 0) {
    return true;
  }

  const stationName = getStationFromCatalog(catalog, place.stationId)?.name ?? '';
  const searchableText = normalizeSearchText(
    [place.name, stationName, ...place.tags, place.comment].join(' '),
  );

  return normalisedQuery
    .split(' ')
    .every((queryToken) => searchableText.includes(queryToken));
}

export function placeMatchesFilters(
  place: Place,
  filters: PlaceFilters,
): boolean {
  if (
    filters.statuses !== undefined &&
    filters.statuses.length > 0 &&
    !filters.statuses.includes(place.status)
  ) {
    return false;
  }

  if (
    filters.returnIntents !== undefined &&
    filters.returnIntents.length > 0 &&
    (place.returnIntent === null ||
      !filters.returnIntents.includes(place.returnIntent))
  ) {
    return false;
  }

  if (filters.tags !== undefined && filters.tags.length > 0) {
    const placeTags = new Set(place.tags.map(normalizeSearchText));
    const hasSelectedTag = filters.tags.some((tag) =>
      placeTags.has(normalizeSearchText(tag)),
    );

    if (!hasSelectedTag) {
      return false;
    }
  }

  return true;
}

export function searchPlaces(
  places: readonly Place[],
  query: string,
  catalog: StationCatalog,
): Place[] {
  return places.filter((place) => placeMatchesSearch(place, query, catalog));
}

export function filterPlaces(
  places: readonly Place[],
  filters: PlaceFilters,
): Place[] {
  return places.filter((place) => placeMatchesFilters(place, filters));
}

export function searchAndFilterPlaces(
  places: readonly Place[],
  query: string,
  filters: PlaceFilters,
  catalog: StationCatalog,
): Place[] {
  return places.filter(
    (place) =>
      placeMatchesSearch(place, query, catalog) &&
      placeMatchesFilters(place, filters),
  );
}

export function hasActivePlaceFilters(filters: PlaceFilters): boolean {
  return Boolean(
    filters.statuses?.length ||
      filters.returnIntents?.length ||
      filters.tags?.length,
  );
}

export function collectPlaceTags(places: readonly Place[]): string[] {
  const result: string[] = [];
  const seen = new Set<string>();

  for (const place of places) {
    for (const tag of place.tags) {
      const key = normalizeSearchText(tag);
      if (key.length > 0 && !seen.has(key)) {
        seen.add(key);
        result.push(tag);
      }
    }
  }

  return result.sort((left, right) =>
    left.localeCompare(right, 'ru-RU', { sensitivity: 'base' }),
  );
}
